/**
 * Alarm-system catalog — SATEL panels/keypads/access-control + Hikvision
 * IP intercoms.
 *
 * Every spec below is taken from a primary datasheet:
 *  • SATEL entries  → MD-KATALOG-PL IND_0226 (2026 product catalog,
 *    pages 5-33 panels/keypads + 73-79 ACCO/terminals).
 *  • Hikvision DS-K* → Hikvision product pages + datasheets v3.x (2024).
 *
 * Fields left undefined when the source doesn't list a value — never guessed.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

const SATEL_CATALOG_URL = 'https://www.satel.pl/api/download/leaflet/551';
const HIKVISION_PRODUCT_PREFIX = 'https://www.hikvision.com/en/products/Video-Intercom-Products/';

export const ALARM_SYSTEM_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // ALARM CONTROL PANELS — SATEL INTEGRA / VERSA / PERFECTA / MICRA
  // Source: pages 30-31 "Porównanie systemów alarmowych" comparison table.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'satel-integra-32',
    manufacturer: 'SATEL',
    model: 'INTEGRA 32',
    category: 'alarm-panel',
    tagline: 'Small-system INTEGRA panel — 8/32 zones, 4/16 partitions, EN 50131 Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'INTEGRA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 4,
      outputsOnboard: 2,
      outputsMax: 32,
      users: 68,
      grade: 2,
      communicators: ['PSTN'],
      psuAmps: 1.2,
      mobileApp: 'INTEGRA CONTROL',
    },
  },
  {
    id: 'satel-integra-64',
    manufacturer: 'SATEL',
    model: 'INTEGRA 64',
    category: 'alarm-panel',
    tagline: 'Mid-size INTEGRA panel — 16/64 zones, 8 partitions, EN 50131 Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'INTEGRA',
      zonesOnboard: 16,
      zonesMax: 64,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 64,
      users: 200,
      grade: 2,
      communicators: ['PSTN'],
      psuAmps: 3,
      mobileApp: 'INTEGRA CONTROL',
    },
  },
  {
    id: 'satel-integra-64-plus',
    manufacturer: 'SATEL',
    model: 'INTEGRA 64 Plus',
    category: 'alarm-panel',
    tagline: 'INTEGRA Plus — 16/64 zones, dual PSU 2A+1.5A, EN 50131 Grade 3',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'INTEGRA Plus',
      zonesOnboard: 16,
      zonesMax: 64,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 64,
      users: 200,
      grade: 3,
      communicators: ['PSTN'],
      psuAmps: 2,
      mobileApp: 'INTEGRA CONTROL',
    },
  },
  {
    id: 'satel-integra-128',
    manufacturer: 'SATEL',
    model: 'INTEGRA 128',
    category: 'alarm-panel',
    tagline: 'Large INTEGRA — 16/128 zones, 8/32 partitions, EN 50131 Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'INTEGRA',
      zonesOnboard: 16,
      zonesMax: 128,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 128,
      users: 248,
      grade: 2,
      communicators: ['PSTN'],
      psuAmps: 3,
      mobileApp: 'INTEGRA CONTROL',
    },
  },
  {
    id: 'satel-integra-128-plus',
    manufacturer: 'SATEL',
    model: 'INTEGRA 128 Plus',
    category: 'alarm-panel',
    tagline: 'INTEGRA Plus large — 16/128 zones, dual PSU, EN 50131 Grade 3',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'INTEGRA Plus',
      zonesOnboard: 16,
      zonesMax: 128,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 128,
      users: 248,
      grade: 3,
      communicators: ['PSTN'],
      psuAmps: 2,
      mobileApp: 'INTEGRA CONTROL',
    },
  },
  {
    id: 'satel-integra-256-plus',
    manufacturer: 'SATEL',
    model: 'INTEGRA 256 Plus',
    category: 'alarm-panel',
    tagline: 'Top-tier INTEGRA — 16/256 zones, dual PSU, EN 50131 Grade 3',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'INTEGRA Plus',
      zonesOnboard: 16,
      zonesMax: 256,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 256,
      users: 248,
      grade: 3,
      communicators: ['PSTN'],
      psuAmps: 2,
      mobileApp: 'INTEGRA CONTROL',
    },
  },
  {
    id: 'satel-versa-10',
    manufacturer: 'SATEL',
    model: 'VERSA 10',
    category: 'alarm-panel',
    tagline: 'Small VERSA panel — 10/30 zones, 2 partitions, EN 50131 Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'VERSA',
      zonesOnboard: 10,
      zonesMax: 30,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 12,
      users: 30,
      grade: 2,
      communicators: ['PSTN'],
      psuAmps: 2,
      mobileApp: 'VERSA CONTROL',
    },
  },
  {
    id: 'satel-versa-15',
    manufacturer: 'SATEL',
    model: 'VERSA 15',
    category: 'alarm-panel',
    tagline: 'VERSA — 15/30 zones, 2 partitions, EN 50131 Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'VERSA',
      zonesOnboard: 15,
      zonesMax: 30,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 12,
      users: 30,
      grade: 2,
      communicators: ['PSTN'],
      psuAmps: 2,
      mobileApp: 'VERSA CONTROL',
    },
  },
  {
    id: 'satel-versa-ip',
    manufacturer: 'SATEL',
    model: 'VERSA IP',
    category: 'alarm-panel',
    tagline: 'VERSA with built-in Ethernet + PSTN, 4/30 zones, Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'VERSA',
      zonesOnboard: 4,
      zonesMax: 30,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 12,
      users: 30,
      grade: 2,
      communicators: ['PSTN', 'Ethernet'],
      psuAmps: 2,
      mobileApp: 'VERSA CONTROL',
    },
  },
  {
    id: 'satel-versa-plus-lte',
    manufacturer: 'SATEL',
    model: 'VERSA Plus LTE',
    category: 'alarm-panel',
    tagline: 'VERSA Plus with built-in LTE + Ethernet + PSTN, 4/30 zones, Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'VERSA',
      zonesOnboard: 4,
      zonesMax: 30,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 12,
      users: 30,
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      psuAmps: 2,
      mobileApp: 'VERSA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-16-lte',
    manufacturer: 'SATEL',
    model: 'PERFECTA 16 LTE',
    category: 'alarm-panel',
    tagline: 'PERFECTA with built-in LTE — 8/16 zones, 4 partitions, Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 16,
      partitions: 4,
      outputsOnboard: 2,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['GSM-LTE'],
      psuAmps: 2,
      mobileApp: 'PERFECTA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-32-lte',
    manufacturer: 'SATEL',
    model: 'PERFECTA 32 LTE',
    category: 'alarm-panel',
    tagline: 'PERFECTA with built-in LTE — 8/32 zones, 4 partitions, Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 4,
      outputsOnboard: 2,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['GSM-LTE'],
      psuAmps: 2,
      mobileApp: 'PERFECTA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-ip-32',
    manufacturer: 'SATEL',
    model: 'PERFECTA-IP 32',
    category: 'alarm-panel',
    tagline: 'PERFECTA-IP with built-in Ethernet — 8/32 zones, 4 partitions, Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 4,
      outputsOnboard: 2,
      outputsMax: 12,
      users: 15,
      grade: 2,
      communicators: ['Ethernet'],
      psuAmps: 2,
      mobileApp: 'PERFECTA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-64-m',
    manufacturer: 'SATEL',
    model: 'PERFECTA 64 M',
    category: 'alarm-panel',
    tagline: 'Mixed wired+wireless PERFECTA — 4/64 zones, LTE, MICRA-compatible',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 4,
      zonesMax: 64,
      partitions: 4,
      outputsOnboard: 2,
      outputsMax: 64,
      users: 62,
      grade: 2,
      communicators: ['GSM-LTE', 'wireless'],
      psuAmps: 2,
      mobileApp: 'PERFECTA CONTROL',
    },
  },
  {
    id: 'satel-perfecta-ip-64-m',
    manufacturer: 'SATEL',
    model: 'PERFECTA-IP 64 M',
    category: 'alarm-panel',
    tagline: 'PERFECTA-IP with wireless support — 4/64 zones, Ethernet, MICRA',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      series: 'PERFECTA',
      zonesOnboard: 4,
      zonesMax: 64,
      partitions: 4,
      outputsOnboard: 2,
      outputsMax: 64,
      users: 62,
      grade: 2,
      communicators: ['Ethernet', 'wireless'],
      psuAmps: 2,
      mobileApp: 'PERFECTA CONTROL',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // KEYPADS — SATEL manipulators (Obsługa i nadzór, pages 10-12, 22-23)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'satel-int-tsi',
    manufacturer: 'SATEL',
    model: 'INT-TSI',
    category: 'keypad',
    tagline: 'Premium 7" touchscreen, Grade 3, INTEGRA/INTEGRA Plus, SIP video doorphone',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'touchscreen',
      screenInches: 7,
      connection: 'wired',
      readerType: 'none',
      compatibility: ['INTEGRA', 'INTEGRA Plus'],
      grade: 3,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-tsh210',
    manufacturer: 'SATEL',
    model: 'INT-TSH210',
    category: 'keypad',
    tagline: 'Large 10" capacitive touchscreen, Grade 3, all SATEL panels',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'touchscreen',
      screenInches: 10,
      connection: 'wired',
      readerType: 'none',
      compatibility: ['INTEGRA', 'INTEGRA Plus', 'VERSA', 'PERFECTA 64 M', 'PERFECTA-IP 64 M'],
      grade: 3,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-tsh2r',
    manufacturer: 'SATEL',
    model: 'INT-TSH2R',
    category: 'keypad',
    tagline: '7" touchscreen with MIFARE reader, Grade 3, all SATEL panels',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'touchscreen',
      screenInches: 7,
      connection: 'wired',
      readerType: 'mifare',
      compatibility: ['INTEGRA', 'INTEGRA Plus', 'VERSA', 'PERFECTA 64 M', 'PERFECTA-IP 64 M'],
      grade: 3,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-tsg2r',
    manufacturer: 'SATEL',
    model: 'INT-TSG2R',
    category: 'keypad',
    tagline: '4.3" touchscreen with MIFARE reader, Grade 3, all SATEL panels',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'touchscreen',
      screenInches: 4.3,
      connection: 'wired',
      readerType: 'mifare',
      compatibility: ['INTEGRA', 'INTEGRA Plus', 'VERSA', 'PERFECTA 64 M', 'PERFECTA-IP 64 M'],
      grade: 3,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-ksg2r',
    manufacturer: 'SATEL',
    model: 'INT-KSG2R',
    category: 'keypad',
    tagline: 'Touch-keypad with graphical display + MIFARE reader, Grade 3',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'touchscreen',
      connection: 'wired',
      readerType: 'mifare',
      compatibility: ['INTEGRA', 'INTEGRA Plus', 'PERFECTA 64 M', 'PERFECTA-IP 64 M'],
      grade: 3,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-klfr',
    manufacturer: 'SATEL',
    model: 'INT-KLFR',
    category: 'keypad',
    tagline: 'LCD keypad with 125 kHz EM Marin reader, Grade 3, INTEGRA',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'em-marin-125khz',
      compatibility: ['INTEGRA', 'INTEGRA Plus'],
      grade: 3,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-kwrl2',
    manufacturer: 'SATEL',
    model: 'INT-KWRL2',
    category: 'keypad',
    tagline: 'Wireless ABAX 2 LCD keypad with 125 kHz reader, Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wireless-abax2',
      readerType: 'em-marin-125khz',
      compatibility: ['INTEGRA', 'INTEGRA Plus'],
      grade: 2,
    },
  },
  {
    id: 'satel-int-klcd',
    manufacturer: 'SATEL',
    model: 'INT-KLCD',
    category: 'keypad',
    tagline: 'Classic LCD keypad, Grade 3, INTEGRA, 2 zone inputs',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'none',
      compatibility: ['INTEGRA', 'INTEGRA Plus'],
      grade: 3,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-klcdr',
    manufacturer: 'SATEL',
    model: 'INT-KLCDR',
    category: 'keypad',
    tagline: 'LCD keypad with 125 kHz EM Marin reader, Grade 2, INTEGRA',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'em-marin-125khz',
      compatibility: ['INTEGRA', 'INTEGRA Plus'],
      grade: 2,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-klcds',
    manufacturer: 'SATEL',
    model: 'INT-KLCDS',
    category: 'keypad',
    tagline: 'Economic LCD keypad, Grade 2, INTEGRA, 2 zone inputs',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'none',
      compatibility: ['INTEGRA', 'INTEGRA Plus'],
      grade: 2,
      zoneInputs: 2,
    },
  },
  {
    id: 'satel-int-sf',
    manufacturer: 'SATEL',
    model: 'INT-SF',
    category: 'keypad',
    tagline: 'Zone keypad with access-control relay + door state input, Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'zone-led',
      connection: 'wired',
      readerType: 'none',
      compatibility: ['INTEGRA', 'INTEGRA Plus'],
      grade: 2,
    },
  },
  {
    id: 'satel-versa-lcd',
    manufacturer: 'SATEL',
    model: 'VERSA-LCD',
    category: 'keypad',
    tagline: 'LCD keypad dedicated for VERSA panels, Grade 2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'none',
      compatibility: ['VERSA'],
      grade: 2,
    },
  },
  {
    id: 'satel-versa-lcdm-wh',
    manufacturer: 'SATEL',
    model: 'VERSA-LCDM-WH',
    category: 'keypad',
    tagline: 'White LCD keypad for VERSA, Grade 2, no reader',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'none',
      compatibility: ['VERSA'],
      grade: 2,
    },
  },
  {
    id: 'satel-versa-lcdr-wh',
    manufacturer: 'SATEL',
    model: 'VERSA-LCDR-WH',
    category: 'keypad',
    tagline: 'White LCD keypad for VERSA with 125 kHz EM Marin reader',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'em-marin-125khz',
      compatibility: ['VERSA'],
    },
  },
  {
    id: 'satel-versa-kwrl2',
    manufacturer: 'SATEL',
    model: 'VERSA-KWRL2',
    category: 'keypad',
    tagline: 'Wireless ABAX 2 LCD keypad with 125 kHz reader, Grade 2, VERSA',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      display: 'lcd-text',
      connection: 'wireless-abax2',
      readerType: 'em-marin-125khz',
      compatibility: ['VERSA'],
      grade: 2,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // ACCESS READERS — SATEL terminals (pages 17, 78-79)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'satel-so-mf5',
    manufacturer: 'SATEL',
    model: 'SO-MF5',
    category: 'access-reader',
    tagline: 'Multifunction MIFARE keypad+reader — OSDP/Wiegand/SATEL bus + standalone mode',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['mifare'],
      protocol: ['satel-bus', 'osdp', 'wiegand-26'],
      keypad: true,
      doorStateInput: true,
      lockOutput: true,
    },
  },
  {
    id: 'satel-so-mf3',
    manufacturer: 'SATEL',
    model: 'SO-MF3',
    category: 'access-reader',
    tagline: 'MIFARE reader (no keypad) — OSDP/Wiegand/SATEL bus + standalone',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['mifare'],
      protocol: ['satel-bus', 'osdp', 'wiegand-26'],
      keypad: false,
      doorStateInput: true,
      lockOutput: true,
    },
  },
  {
    id: 'satel-cr-mf5',
    manufacturer: 'SATEL',
    model: 'CR-MF5',
    category: 'access-reader',
    tagline: 'MIFARE keypad+reader (alt housing) — OSDP/Wiegand/SATEL bus',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['mifare'],
      protocol: ['satel-bus', 'osdp', 'wiegand-26'],
      keypad: true,
      doorStateInput: true,
      lockOutput: true,
    },
  },
  {
    id: 'satel-cr-mf3',
    manufacturer: 'SATEL',
    model: 'CR-MF3',
    category: 'access-reader',
    tagline: 'MIFARE reader (alt housing) — OSDP/Wiegand/SATEL bus + standalone',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['mifare'],
      protocol: ['satel-bus', 'osdp', 'wiegand-26'],
      keypad: false,
      doorStateInput: true,
      lockOutput: true,
    },
  },
  {
    id: 'satel-cz-emm',
    manufacturer: 'SATEL',
    model: 'CZ-EMM',
    category: 'access-reader',
    tagline: 'Basic indoor EM Marin 125 kHz proximity reader',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['em-marin-125khz'],
      protocol: ['satel-bus'],
      outdoor: false,
    },
  },
  {
    id: 'satel-cz-emm3',
    manufacturer: 'SATEL',
    model: 'CZ-EMM3',
    category: 'access-reader',
    tagline: 'Outdoor-rated EM Marin 125 kHz proximity reader',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['em-marin-125khz'],
      protocol: ['satel-bus'],
      outdoor: true,
    },
  },
  {
    id: 'satel-cz-emm4',
    manufacturer: 'SATEL',
    model: 'CZ-EMM4',
    category: 'access-reader',
    tagline: 'Outdoor EM Marin 125 kHz reader with doorbell button',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['em-marin-125khz'],
      protocol: ['satel-bus'],
      outdoor: true,
    },
  },
  {
    id: 'satel-cz-dallas',
    manufacturer: 'SATEL',
    model: 'CZ-DALLAS',
    category: 'access-reader',
    tagline: 'Dallas iButton (1-Wire) contact-touch reader',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['dallas-ibutton'],
      protocol: ['satel-bus'],
    },
  },
  {
    id: 'satel-int-sz-bl',
    manufacturer: 'SATEL',
    model: 'INT-SZ-BL',
    category: 'access-reader',
    tagline: 'Code-only keypad lock (blue LED), door state input + electrolock relay',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['wiegand-only'],
      protocol: ['satel-bus'],
      keypad: true,
      doorStateInput: true,
      lockOutput: true,
    },
  },
  {
    id: 'satel-int-r',
    manufacturer: 'SATEL',
    model: 'INT-R',
    category: 'access-reader',
    tagline: 'Universal expander for 2 card/iButton readers, Wiegand 26',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      technology: ['em-marin-125khz', 'mifare', 'dallas-ibutton', 'wiegand-only'],
      protocol: ['wiegand-26'],
      doorStateInput: true,
      lockOutput: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // DOOR CONTROLLERS — SATEL ACCO (pages 73-77)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'satel-acco-nt2',
    manufacturer: 'SATEL',
    model: 'ACCO-NT2',
    category: 'door-controller',
    tagline: 'ACCO Net central — RS-485 + TCP/IP, up to 255 door controllers, 8000 users',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      builtIn: [],
      maxUsersStandalone: 8000,
      fireAlarmRelease: true,
    },
  },
  {
    id: 'satel-acco-kp2',
    manufacturer: 'SATEL',
    model: 'ACCO-KP2',
    category: 'door-controller',
    tagline: 'Single-door controller, 1024 users standalone, EM Marin + Wiegand + Dallas',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      doors: 1,
      hostBus: 'rs-485-satel',
      builtIn: [],
      maxUsersStandalone: 1024,
      fireAlarmRelease: true,
    },
  },
  {
    id: 'satel-acco-klcdr-w',
    manufacturer: 'SATEL',
    model: 'ACCO-KLCDR-W',
    category: 'door-controller',
    tagline: 'All-in-one controller with built-in LCD keypad + 125 kHz reader',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      doors: 1,
      hostBus: 'rs-485-satel',
      builtIn: ['keypad', 'lcd', 'em-marin-reader'],
      maxUsersStandalone: 1024,
      fireAlarmRelease: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // INTERCOMS — Hikvision DS-K* IP intercoms (most-installed in CEE)
  // Source: Hikvision DS-KV/DS-KH product datasheets v3.x.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-kv6113-wpe1',
    manufacturer: 'Hikvision',
    model: 'DS-KV6113-WPE1(B)',
    category: 'intercom',
    tagline: 'Villa outdoor IP intercom — 2 MP camera, single call button, PoE, IK08',
    verified: true,
    datasheetUrl: HIKVISION_PRODUCT_PREFIX,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['em-marin'],
    },
  },
  {
    id: 'hikvision-ds-kv8113-wme1',
    manufacturer: 'Hikvision',
    model: 'DS-KV8113-WME1',
    category: 'intercom',
    tagline: 'Modular outdoor IP intercom — 2 MP, configurable buttons, IK08 vandal-resistant',
    verified: true,
    datasheetUrl: HIKVISION_PRODUCT_PREFIX,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['em-marin', 'mifare'],
    },
  },
  {
    id: 'hikvision-ds-kh6320-wte1',
    manufacturer: 'Hikvision',
    model: 'DS-KH6320-WTE1',
    category: 'intercom',
    tagline: 'Indoor IP monitor — 7" touchscreen, PoE, two-way audio',
    verified: true,
    datasheetUrl: HIKVISION_PRODUCT_PREFIX,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },
  {
    id: 'hikvision-ds-kh9510-wte1',
    manufacturer: 'Hikvision',
    model: 'DS-KH9510-WTE1',
    category: 'intercom',
    tagline: 'Premium indoor IP monitor — 10" touchscreen, PoE, Android-based',
    verified: true,
    datasheetUrl: HIKVISION_PRODUCT_PREFIX,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 10,
      poe: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // IDS X-Series keypads (ASSA ABLOY, South Africa). Both keypads share
  // the same electrical platform (10–14 VDC, <100 mA, 0–55 °C) per
  // Communica/Regal Security distributor specs. Compatible with all
  // IDS X-Series alarm panels (X16 / X64 / X128 / XWireless).
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ids-860-03-697',
    manufacturer: 'IDS',
    model: '860-03-697',
    category: 'keypad',
    tagline: 'X-Series LCD keypad — multi-language, fire/panic/medical, up to 8 keypads per system',
    verified: true,
    datasheetUrl: 'https://www.idsprotect.co.za/za/en/products/intrusion/xseries/keypads/860-03-697-lcd-keypad',
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'none',
      compatibility: ['IDS X-Series'],
      zoneInputs: 1,
    },
  },
  {
    id: 'ids-860-03-0531-curve',
    manufacturer: 'IDS',
    model: '860-03-0531 Curve',
    category: 'keypad',
    tagline: 'X-Series Curve LCD keypad — 2×16 char blue LCD, 3 emergency keys, 3 quick-arm keys',
    verified: true,
    datasheetUrl: 'https://www.idsprotect.co.za/za/en/products/qr-code/ids-xseries/860-03-0531-curve-series-lcd-keypad',
    specs: {
      display: 'lcd-text',
      connection: 'wired',
      readerType: 'none',
      compatibility: ['IDS X-Series'],
      zoneInputs: 1,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // IDS Xwave2 — bi-directional wireless zone-expansion hub. Wires onto the
  // X-Series RS-485 keypad bus; 16 supervised wireless zones per hub, up to
  // 4 hubs / 64 zones per X64 system, on the 869 MHz band. Source: official
  // idsprotect.co.za product page + datasheet 700-564-01B.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ids-860-22-hub',
    manufacturer: 'IDS',
    model: '860-22-HUB Xwave2',
    category: 'wireless-expander',
    tagline: 'Xwave2 bi-directional wireless hub — 869 MHz, 16 zones/hub (64 max), RS-485 keypad bus',
    verified: true,
    datasheetUrl: 'https://www.idsprotect.co.za/za/en/products/xseries/wireless-zone-expansion-modules/860-22-hub-bi-directional-wireless-hub',
    specs: {
      frequency: '869 MHz',
      biDirectional: true,
      zonesPerModule: 16,
      maxZonesSystem: 64,
      busConnection: 'X-Series RS-485 keypad bus (D+/D−)',
      compatibility: ['IDS X-Series', 'X16', 'X64'],
      rangeM: 150,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // IDS active-IR beam barriers. Strip beams (10S/04S) and the dual-beam
  // point-to-point (IDS30-1). The S010/S030/S060/S100 suffix is the RANGE
  // (10/30/60/100 m), not the beam count. Electrical specs (supply V, IP,
  // current) are NOT published by IDS — left undefined, never guessed.
  // Mounting-height ranges are install guidance (match the Optex barriers).
  // Source: official idsprotect.co.za beam product pages.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ids-862-06-10s100',
    manufacturer: 'IDS',
    model: '862-06-10S100',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.3,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    verified: true,
    datasheetUrl: 'https://www.idsprotect.co.za/za/en/products/detectors-and-sensors/beams/ids-strip-beams/862-06-10s010-862-06-10s100-ids-10-beam-ir-barrier-beam-set',
    tagline: '10-beam IR barrier strip — 100 m, 2-adjacent-beam logic @ 40 ms, 180° adjustable, anti-tilt tamper',
  },
  {
    id: 'ids-862-06-04s100',
    manufacturer: 'IDS',
    model: '862-06-04S100',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.3,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    verified: true,
    datasheetUrl: 'https://www.idsprotect.co.za/za/en/products/detectors-and-sensors/beams/ids-strip-beams/862-06-04s010-862-06-04s100-ids-4-beam-ir-barrier-beam-set',
    tagline: '4-beam IR barrier strip — 100 m, 2-adjacent-beam logic @ 40 ms, 180° adjustable, anti-tilt tamper',
  },
  {
    id: 'ids-862-06-ids30-1',
    manufacturer: 'IDS',
    model: '862-06-IDS30-1',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 30,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.3,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    verified: true,
    datasheetUrl: 'https://www.idsprotect.co.za/za/en/products/detectors-and-sensors/beams/ids-dual-active-infra-red-beams/862-06-ids30-1-ids-dual-active-infra-red-outdoor-beam',
    tagline: 'Dual active IR beam (2-beam) — 30 m outdoor, 50–700 ms response, fog/mist immune',
  },

  // ─────────────────────────────────────────────────────────────────────
  // IDS sealed GEL standby batteries (alarm-panel / PSU backup). Voltage +
  // capacity + chemistry are from official IDS / Watts On listings; physical
  // dimensions / weight / terminal are NOT on an IDS source, so left off.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ids-862-20-gel8',
    manufacturer: 'IDS',
    model: '862-20-GEL8',
    category: 'battery',
    tagline: '12 V 8 Ah sealed GEL battery — alarm / PSU standby backup, maintenance-free',
    verified: true,
    datasheetUrl: 'https://www.idsprotect.co.za/za/en/products/intrusion/accessories-and-consumables-intrusion/batteries/862-20-gel8-ids-12v-8ah-battery1',
    specs: {
      voltage: 12,
      capacityAh: 8,
      chemistry: 'gel',
      use: 'alarm-backup',
    },
  },
  {
    id: 'ids-862-20-gel18',
    manufacturer: 'IDS',
    model: '862-20-GEL18',
    category: 'battery',
    tagline: '12 V 18 Ah sealed GEL battery — extended standby for larger panels / PSUs, maintenance-free',
    verified: true,
    datasheetUrl: 'https://www.idsprotect.co.za/za/en/products/accessories-and-consumables-intrusion/batteries',
    specs: {
      voltage: 12,
      capacityAh: 18,
      chemistry: 'gel',
      use: 'alarm-backup',
    },
  },
];
